import { jsPDF } from 'jspdf';

export interface PolicyPdfColorBlock {
  key: 'red' | 'orange' | 'yellow' | 'green' | 'blue';
  label: string;
  accent: string;
  tint: string;
  title: string;
  description: string;
}

const PDF_POLICY_PALETTE: Record<PolicyPdfColorBlock['key'], { accent: [number, number, number]; tint: [number, number, number] }> = {
  red: { accent: [239, 68, 68], tint: [245, 239, 239] },
  orange: { accent: [245, 158, 11], tint: [245, 241, 234] },
  yellow: { accent: [234, 179, 8], tint: [243, 242, 219] },
  green: { accent: [34, 197, 94], tint: [236, 242, 238] },
  blue: { accent: [59, 130, 246], tint: [236, 241, 248] }
};

type PdfWithFontApi = jsPDF & {
  addFileToVFS: (fileName: string, fileData: string) => void;
  addFont: (fileName: string, fontName: string, fontStyle: string) => void;
};

const parseColorToRgb = (input: string): [number, number, number] => {
  const value = String(input || '').trim();
  const hexMatch = value.match(/^#([0-9a-f]{6})$/i);
  if (hexMatch) {
    const hex = hexMatch[1];
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16)
    ];
  }
  const rgbaMatch = value.match(/^rgba?\(([^)]+)\)$/i);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(',').map((part) => Number(part.trim()));
    return [
      Number.isFinite(parts[0]) ? Math.max(0, Math.min(255, Math.round(parts[0]))) : 255,
      Number.isFinite(parts[1]) ? Math.max(0, Math.min(255, Math.round(parts[1]))) : 255,
      Number.isFinite(parts[2]) ? Math.max(0, Math.min(255, Math.round(parts[2]))) : 255
    ];
  }
  return [243, 244, 246];
};

const resolvePdfColors = (block: PolicyPdfColorBlock) => {
  const preset = PDF_POLICY_PALETTE[block.key];
  if (preset) return preset;
  return {
    tint: parseColorToRgb(block.tint),
    accent: parseColorToRgb(block.accent)
  };
};

const toLines = (value: string | string[]) => (Array.isArray(value) ? value : [String(value)]);

const truncateLines = (lines: string[], maxLines: number) => {
  if (maxLines <= 0) return [];
  if (lines.length <= maxLines) return lines;
  const next = lines.slice(0, maxLines);
  const lastIndex = maxLines - 1;
  const last = next[lastIndex] || '';
  next[lastIndex] = `${last.replace(/\s+$/, '')}...`;
  return next;
};

const sanitizePdfText = (input: string) => String(input || '')
  .replace(/[“”]/g, '"')
  .replace(/[‘’]/g, "'")
  .replace(/[–—]/g, '-')
  .replace(/\u2026/g, '...')
  .replace(/[^\x20-\x7E\n\t]/g, ' ');

let ralewayRegularBase64Promise: Promise<string> | null = null;
let ralewayBoldBase64Promise: Promise<string> | null = null;

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

const loadFontBase64 = (fontPath: string) => fetch(fontPath)
  .then(async (response) => {
    if (!response.ok) {
      throw new Error(`Failed to load font file: ${fontPath}`);
    }
    return arrayBufferToBase64(await response.arrayBuffer());
  });

const ensureRalewayFontRegistered = async (pdf: jsPDF) => {
  if (!ralewayRegularBase64Promise) {
    ralewayRegularBase64Promise = loadFontBase64('/fonts/Raleway-Regular.ttf');
  }
  if (!ralewayBoldBase64Promise) {
    ralewayBoldBase64Promise = loadFontBase64('/fonts/Raleway-SemiBold.ttf');
  }
  const [regularBase64, boldBase64] = await Promise.all([ralewayRegularBase64Promise, ralewayBoldBase64Promise]);
  const pdfApi = pdf as PdfWithFontApi;
  pdfApi.addFileToVFS('Raleway-Regular.ttf', regularBase64);
  pdfApi.addFont('Raleway-Regular.ttf', 'Raleway', 'normal');
  pdfApi.addFileToVFS('Raleway-SemiBold.ttf', boldBase64);
  pdfApi.addFont('Raleway-SemiBold.ttf', 'Raleway', 'bold');
};

const loadLogoDataUrl = async () => {
  try {
    const response = await fetch('/icon.png');
    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to read logo image.'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return '';
  }
};

export const exportPolicyToPdf = async ({
  title,
  subtitle,
  colorBlocks,
  fileNamePrefix
}: {
  title?: string;
  subtitle?: string;
  colorBlocks: PolicyPdfColorBlock[];
  fileNamePrefix?: string;
}) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'a4'
  });
  let fontFamily: 'Raleway' | 'helvetica' = 'helvetica';
  try {
    await ensureRalewayFontRegistered(pdf);
    pdf.setFont('Raleway', 'normal');
    pdf.setFontSize(11);
    // Probe font metrics early so we can fallback before rendering.
    pdf.getTextWidth('Traffic Lite PDF');
    fontFamily = 'Raleway';
  } catch (error) {
    console.warn('Falling back to helvetica for PDF export:', error);
    fontFamily = 'helvetica';
  }

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const horizontalPadding = 28;
  const bottomPadding = 24;
  const bottomLimit = pageHeight - bottomPadding;
  let cursorY = 30;

  const logoDataUrl = await loadLogoDataUrl();
  if (logoDataUrl) {
    pdf.addImage(logoDataUrl, 'PNG', horizontalPadding, cursorY - 2, 18, 18, undefined, 'FAST');
  }
  pdf.setFont(fontFamily, 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(17, 24, 39);
  pdf.text('Traffic Lite', horizontalPadding + 24, cursorY + 10);
  cursorY += 28;

  if (title?.trim()) {
    pdf.setFont(fontFamily, 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(17, 24, 39);
    const titleLines = toLines(pdf.splitTextToSize(sanitizePdfText(title.trim()), pageWidth - horizontalPadding * 2));
    pdf.text(titleLines, pageWidth / 2, cursorY, { align: 'center' });
    cursorY += titleLines.length * 18;
  }

  if (subtitle?.trim()) {
    pdf.setFont(fontFamily, 'normal');
    pdf.setFontSize(11);
    pdf.setTextColor(55, 65, 81);
    const subtitleLines = toLines(pdf.splitTextToSize(sanitizePdfText(subtitle.trim()), pageWidth - horizontalPadding * 2));
    pdf.text(subtitleLines, pageWidth / 2, cursorY, { align: 'center' });
    cursorY += subtitleLines.length * 13 + 6;
  }

  cursorY += 4;

  const contentWidth = pageWidth - horizontalPadding * 2;
  const buildMetrics = (scale: number) => {
    const cardRadius = 12;
    const stripeWidth = 10;
    const cardGap = Math.max(6, Math.round(9 * scale));
    const cardPaddingX = Math.max(11, Math.round(14 * scale));
    const labelTopOffset = Math.max(14, Math.round(16 * scale));
    const metaTopOffset = labelTopOffset + Math.max(14, Math.round(15 * scale));
    const bodyTopOffset = metaTopOffset + Math.max(13, Math.round(14 * scale));
    const bodyBottomPadding = Math.max(10, Math.round(12 * scale));
    const bodyLineHeight = Math.max(11.4, 12.6 * scale);
    const sectionTitleFontSize = Math.max(8.4, 9.4 * scale);
    const sectionBodyFontSize = Math.max(9.4, 10.8 * scale);
    const labelFontSize = Math.max(9.8, 11 * scale);
    const titleColumnWidth = Math.max(124, Math.round(contentWidth * 0.285));
    const columnGap = Math.max(14, Math.round(18 * scale));
    const descriptionColumnWidth = contentWidth - stripeWidth - cardPaddingX * 2 - titleColumnWidth - columnGap;
    const minBoxHeight = Math.max(72, Math.round(84 * scale));
    return {
      cardRadius,
      stripeWidth,
      cardGap,
      cardPaddingX,
      labelTopOffset,
      metaTopOffset,
      bodyTopOffset,
      bodyBottomPadding,
      bodyLineHeight,
      sectionTitleFontSize,
      sectionBodyFontSize,
      labelFontSize,
      titleColumnWidth,
      columnGap,
      descriptionColumnWidth,
      minBoxHeight
    };
  };

  const computeLayout = (
    metrics: ReturnType<typeof buildMetrics>,
    maxTitleLines: number,
    maxDescriptionLines: number
  ) => {
    const cards = colorBlocks.map((block) => {
      pdf.setFont(fontFamily, 'normal');
      pdf.setFontSize(metrics.sectionBodyFontSize);
      const titleLines = truncateLines(
        toLines(pdf.splitTextToSize(sanitizePdfText(block.title || ''), metrics.titleColumnWidth)),
        maxTitleLines
      );
      const descriptionLines = truncateLines(
        toLines(pdf.splitTextToSize(sanitizePdfText(block.description || ''), metrics.descriptionColumnWidth)),
        maxDescriptionLines
      );
      const maxBodyLines = Math.max(titleLines.length, descriptionLines.length);
      const naturalHeight = Math.max(
        metrics.minBoxHeight,
        metrics.bodyTopOffset + maxBodyLines * metrics.bodyLineHeight + metrics.bodyBottomPadding
      );
      return { block, titleLines, descriptionLines, boxHeight: naturalHeight };
    });
    const totalHeight = cards.reduce((sum, card, index) => (
      sum + card.boxHeight + (index === cards.length - 1 ? 0 : metrics.cardGap)
    ), 0);
    return { cards, totalHeight };
  };

  const availableHeight = Math.max(220, bottomLimit - cursorY);
  const candidateScales = [1, 0.95, 0.9, 0.86, 0.82, 0.78, 0.74];
  let selectedMetrics = buildMetrics(candidateScales[candidateScales.length - 1]);
  let selectedLayout = computeLayout(selectedMetrics, 4, 8);

  for (const scale of candidateScales) {
    const metrics = buildMetrics(scale);
    const layout = computeLayout(metrics, 4, 8);
    if (layout.totalHeight <= availableHeight) {
      selectedMetrics = metrics;
      selectedLayout = layout;
      break;
    }
    selectedMetrics = metrics;
    selectedLayout = layout;
  }

  if (selectedLayout.totalHeight > availableHeight) {
    let fallback: ReturnType<typeof computeLayout> | null = null;
    for (let titleLines = 3; titleLines >= 1; titleLines -= 1) {
      for (let descriptionLines = 7; descriptionLines >= 2; descriptionLines -= 1) {
        const attempt = computeLayout(selectedMetrics, titleLines, descriptionLines);
        if (attempt.totalHeight <= availableHeight) {
          fallback = attempt;
          break;
        }
      }
      if (fallback) break;
    }
    selectedLayout = fallback || computeLayout(selectedMetrics, 1, 2);
  }

  for (const [index, card] of selectedLayout.cards.entries()) {
    const { block, titleLines, descriptionLines, boxHeight } = card;
    const { tint, accent } = resolvePdfColors(block);
    const cardRadius = selectedMetrics.cardRadius;
    const stripeRadius = cardRadius;

    pdf.setFillColor(tint[0], tint[1], tint[2]);
    pdf.setDrawColor(226, 232, 240);
    pdf.roundedRect(horizontalPadding, cursorY, contentWidth, boxHeight, cardRadius, cardRadius, 'FD');

    pdf.setFillColor(accent[0], accent[1], accent[2]);
    pdf.roundedRect(horizontalPadding, cursorY, selectedMetrics.stripeWidth, boxHeight, stripeRadius, stripeRadius, 'F');
    pdf.setFillColor(tint[0], tint[1], tint[2]);
    pdf.rect(horizontalPadding + selectedMetrics.stripeWidth - 2, cursorY, 2, boxHeight, 'F');

    const titleColumnX = horizontalPadding + selectedMetrics.stripeWidth + selectedMetrics.cardPaddingX;
    const descriptionColumnX = titleColumnX + selectedMetrics.titleColumnWidth + selectedMetrics.columnGap;

    pdf.setFont(fontFamily, 'bold');
    pdf.setFontSize(selectedMetrics.labelFontSize);
    pdf.setTextColor(17, 24, 39);
    pdf.text(sanitizePdfText(block.label), titleColumnX, cursorY + selectedMetrics.labelTopOffset);

    pdf.setFont(fontFamily, 'bold');
    pdf.setFontSize(selectedMetrics.sectionTitleFontSize);
    pdf.setTextColor(31, 41, 55);
    pdf.text('TITLE', titleColumnX, cursorY + selectedMetrics.metaTopOffset);
    pdf.text('DESCRIPTION', descriptionColumnX, cursorY + selectedMetrics.metaTopOffset);

    pdf.setFont(fontFamily, 'normal');
    pdf.setFontSize(selectedMetrics.sectionBodyFontSize);
    pdf.setTextColor(17, 24, 39);
    pdf.text(titleLines, titleColumnX, cursorY + selectedMetrics.bodyTopOffset, { lineHeightFactor: 1.18 });
    pdf.text(descriptionLines, descriptionColumnX, cursorY + selectedMetrics.bodyTopOffset, { lineHeightFactor: 1.18 });

    cursorY += boxHeight + (index === selectedLayout.cards.length - 1 ? 0 : selectedMetrics.cardGap);
  }

  const safePrefix = (fileNamePrefix || 'traffic-lite-policy').replace(/[^a-z0-9-_]/gi, '-').toLowerCase();
  const timeStamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  pdf.save(`${safePrefix}-${timeStamp}.pdf`);
};
